/**
 * Supabase Storage utilities for file uploads and management
 */

import { supabase } from './client'

// ============================================================================
// Storage Bucket Constants
// ============================================================================

export const BUCKETS = {
  /** Public bucket for system assets (3D models, icons, reference images) */
  ASSETS: 'assets',
  /** Private bucket for tenant documents (requires auth) */
  FILES: 'files',
} as const

export type BucketName = (typeof BUCKETS)[keyof typeof BUCKETS]

// ============================================================================
// Asset Folder Structure (assets bucket)
// ============================================================================

export const ASSET_FOLDERS = {
  MODELS: 'models',
  ICONS: 'icons',
  IMAGES: 'images',
} as const

// ============================================================================
// File Category Constants (files bucket)
// ============================================================================

export const FILE_CATEGORIES = {
  MAINTENANCE: 'maintenance',
  CONTRACTS: 'contracts',
  INSURANCE: 'insurance',
  LOGS: 'logs',
  PHOTOS: 'photos',
  DOCUMENTS: 'documents',
} as const

export type FileCategory = (typeof FILE_CATEGORIES)[keyof typeof FILE_CATEGORIES]

// ============================================================================
// Types
// ============================================================================

export interface UploadResult {
  path: string
  fullPath: string
  publicUrl?: string
}

export interface UploadOptions {
  /** Cache control header (default: 3600 for 1 hour) */
  cacheControl?: string
  /** Content type override */
  contentType?: string
  /** Upsert file if exists */
  upsert?: boolean
}

// ============================================================================
// Asset Upload Functions (Admin only - assets bucket)
// ============================================================================

/**
 * Upload a file to the assets bucket (admin only)
 * @param file - File to upload
 * @param folder - Folder within assets bucket (e.g., 'models', 'icons')
 * @param filename - Optional custom filename (defaults to original)
 * @param options - Upload options
 */
export async function uploadAsset(
  file: File,
  folder: string,
  filename?: string,
  options: UploadOptions = {}
): Promise<UploadResult> {
  const finalFilename = filename || sanitizeFilename(file.name)
  const path = `${folder}/${finalFilename}`

  const { data, error } = await supabase.storage
    .from(BUCKETS.ASSETS)
    .upload(path, file, {
      cacheControl: options.cacheControl || '3600',
      contentType: options.contentType || file.type,
      upsert: options.upsert ?? true,
    })

  if (error) {
    throw new Error(`Failed to upload asset: ${error.message}`)
  }

  const publicUrl = getAssetPublicUrl(data.path)

  return {
    path: data.path,
    fullPath: data.fullPath,
    publicUrl,
  }
}

/**
 * Upload a 3D aircraft model (GLB/GLTF)
 * @param file - Model file (.glb or .gltf)
 * @param filename - Optional custom filename
 */
export async function uploadAircraftModel(
  file: File,
  filename?: string
): Promise<UploadResult> {
  // Validate file type
  const validTypes = ['model/gltf-binary', 'model/gltf+json', 'application/octet-stream']
  const validExtensions = ['.glb', '.gltf']

  const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))

  if (!validTypes.includes(file.type) && !validExtensions.includes(extension)) {
    throw new Error('Invalid file type. Only GLB and GLTF files are allowed.')
  }

  // Use .glb extension for binary, ensure proper naming
  const finalFilename = filename || sanitizeFilename(file.name)

  return uploadAsset(file, ASSET_FOLDERS.MODELS, finalFilename, {
    contentType: extension === '.gltf' ? 'model/gltf+json' : 'model/gltf-binary',
    upsert: true,
  })
}

/**
 * Get public URL for an asset
 * @param path - Path within assets bucket
 */
export function getAssetPublicUrl(path: string): string {
  const { data } = supabase.storage.from(BUCKETS.ASSETS).getPublicUrl(path)
  return data.publicUrl
}

/**
 * Get public URL for an aircraft model
 * @param filename - Model filename (with or without 'models/' prefix)
 */
export function getModelPublicUrl(filename: string): string {
  const path = filename.startsWith(`${ASSET_FOLDERS.MODELS}/`)
    ? filename
    : `${ASSET_FOLDERS.MODELS}/${filename}`
  return getAssetPublicUrl(path)
}

/**
 * Delete an asset from the assets bucket
 * @param path - Path within assets bucket
 */
export async function deleteAsset(path: string): Promise<void> {
  const { error } = await supabase.storage.from(BUCKETS.ASSETS).remove([path])

  if (error) {
    throw new Error(`Failed to delete asset: ${error.message}`)
  }
}

/**
 * List assets in a folder
 * @param folder - Folder path within assets bucket
 */
export async function listAssets(folder: string): Promise<string[]> {
  const { data, error } = await supabase.storage
    .from(BUCKETS.ASSETS)
    .list(folder, {
      sortBy: { column: 'name', order: 'asc' },
    })

  if (error) {
    throw new Error(`Failed to list assets: ${error.message}`)
  }

  return data.map((file) => `${folder}/${file.name}`)
}

// ============================================================================
// Tenant File Functions (files bucket)
// ============================================================================

/**
 * Build file path for tenant files
 * @param organizationId - Organization/account ID
 * @param locationId - Location ID (optional)
 * @param category - File category
 * @param filename - File name
 */
export function buildTenantFilePath(
  organizationId: string,
  locationId: string | null,
  category: FileCategory,
  filename: string
): string {
  const sanitized = sanitizeFilename(filename)
  if (locationId) {
    return `${organizationId}/${locationId}/${category}/${sanitized}`
  }
  return `${organizationId}/_org/${category}/${sanitized}`
}

/**
 * Upload a file to the tenant's files bucket
 * @param file - File to upload
 * @param organizationId - Organization/account ID
 * @param locationId - Location ID (or null for org-level)
 * @param category - File category
 * @param filename - Optional custom filename
 */
export async function uploadTenantFile(
  file: File,
  organizationId: string,
  locationId: string | null,
  category: FileCategory,
  filename?: string,
  options: UploadOptions = {}
): Promise<UploadResult> {
  const finalFilename = filename || file.name
  const path = buildTenantFilePath(organizationId, locationId, category, finalFilename)

  const { data, error } = await supabase.storage
    .from(BUCKETS.FILES)
    .upload(path, file, {
      cacheControl: options.cacheControl || '3600',
      contentType: options.contentType || file.type,
      upsert: options.upsert ?? false,
    })

  if (error) {
    throw new Error(`Failed to upload file: ${error.message}`)
  }

  return {
    path: data.path,
    fullPath: data.fullPath,
  }
}

/**
 * Get signed URL for a tenant file (valid for specified duration)
 * @param path - Path within files bucket
 * @param expiresIn - Expiration time in seconds (default: 1 hour)
 */
export async function getTenantFileSignedUrl(
  path: string,
  expiresIn: number = 3600
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(BUCKETS.FILES)
    .createSignedUrl(path, expiresIn)

  if (error) {
    throw new Error(`Failed to get signed URL: ${error.message}`)
  }

  return data.signedUrl
}

/**
 * Download a tenant file
 * @param path - Path within files bucket
 */
export async function downloadTenantFile(path: string): Promise<Blob> {
  const { data, error } = await supabase.storage
    .from(BUCKETS.FILES)
    .download(path)

  if (error) {
    throw new Error(`Failed to download file: ${error.message}`)
  }

  return data
}

/**
 * Delete a tenant file
 * @param path - Path within files bucket
 */
export async function deleteTenantFile(path: string): Promise<void> {
  const { error } = await supabase.storage.from(BUCKETS.FILES).remove([path])

  if (error) {
    throw new Error(`Failed to delete file: ${error.message}`)
  }
}

/**
 * List files in a tenant folder
 * @param organizationId - Organization/account ID
 * @param locationId - Location ID (optional)
 * @param category - File category (optional)
 */
export async function listTenantFiles(
  organizationId: string,
  locationId?: string,
  category?: FileCategory
): Promise<string[]> {
  let folder = organizationId
  if (locationId) {
    folder = `${organizationId}/${locationId}`
    if (category) {
      folder = `${folder}/${category}`
    }
  }

  const { data, error } = await supabase.storage
    .from(BUCKETS.FILES)
    .list(folder, {
      sortBy: { column: 'name', order: 'asc' },
    })

  if (error) {
    throw new Error(`Failed to list files: ${error.message}`)
  }

  return data.map((file) => `${folder}/${file.name}`)
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Sanitize filename for safe storage
 * - Removes special characters
 * - Replaces spaces with dashes
 * - Converts to lowercase
 */
export function sanitizeFilename(filename: string): string {
  const extension = filename.slice(filename.lastIndexOf('.'))
  const name = filename.slice(0, filename.lastIndexOf('.'))

  const sanitized = name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()

  return `${sanitized}${extension.toLowerCase()}`
}

/**
 * Get file extension from filename
 */
export function getFileExtension(filename: string): string {
  return filename.slice(filename.lastIndexOf('.')).toLowerCase()
}

/**
 * Validate file size
 * @param file - File to validate
 * @param maxSizeMB - Maximum size in MB
 */
export function validateFileSize(file: File, maxSizeMB: number): boolean {
  const maxBytes = maxSizeMB * 1024 * 1024
  return file.size <= maxBytes
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
